import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { LateFeeNote } from "@/components/site/LateFeeNote";
import { FeesByTerm } from "@/components/site/FeesByTerm";
import { sitePricingQuery } from "@/lib/pricing-query";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { OnboardingNote } from "../../components/site/Highlights";
import { currentSchoolYearLabel } from "@/lib/school-year";
import { safeLoader } from "@/lib/safe-loader";

const title = `Fees for the ${currentSchoolYearLabel()} school year | SIS Estepona`;
const description =
  "Term fees for preschool, primary school, distance studies, leisure activities and Malmö FF's football academy at Swedish International School in Estepona.";

export const Route = createFileRoute("/en/fees")({
  loader: ({ context }) => safeLoader("en.fees", () => context.queryClient.ensureQueryData(sitePricingQuery), { pricing: null, stale: false, fetchedAt: null, error: "unavailable", termSettings: null }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/fees") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/fees"),
  }),
  component: Fees,
});


function Fees() {
  const pricingData = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow="Fees"
        title="Fees and payment plans"
        intro="The fee is paid per term in advance. Payment in instalments may be offered by agreement and is then stated in the agreement. We also offer family discounts for larger families. Please get in touch if you have any questions about the fees."
      />

      <section className="mx-auto max-w-4xl px-5 pt-8">
        <p className="rounded-full border border-primary/15 bg-primary/5 px-5 py-3 text-sm text-foreground/75 shadow-soft">These prices apply to HT26, VT27, HT27 and VT28 and were published on 28 September 2026. New prices for a term are published here no later than 10 January (autumn term) and 1 August (spring term).</p>
      </section>
      <section className="mx-auto max-w-4xl px-5 py-8 sm:py-10">
        <div className="rounded-lg border border-border bg-card p-5 shadow-soft sm:p-8">
        <h2 className="text-2xl font-semibold">Enrolment and discounts</h2>
        <p className="mt-2 text-sm text-muted-foreground italic">
          The enrolment fee is sometimes called a registration fee – it is the same fee.
        </p>
        <div className="mt-6 rounded-md border border-border bg-muted/40 p-5 text-sm leading-relaxed text-foreground/85"><p>The enrolment fee is paid once per pupil and is listed for each term below.</p><p className="mt-3">Short-term registration has a lower enrolment fee, valid for two terms within the same school year.</p></div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2"><div className="rounded-md border border-primary/15 bg-primary/5 p-5"><strong className="text-2xl text-primary">20%</strong><p className="mt-1 text-sm text-foreground/75">Family discount for the third child, applied to the lowest fee.</p></div><div className="rounded-md border border-primary/15 bg-primary/5 p-5"><strong className="text-2xl text-primary">50%</strong><p className="mt-1 text-sm text-foreground/75">Family discount for the fourth child, applied to the lowest fee.</p></div></div>
        </div>

        <h2 className="mt-14 text-2xl font-semibold">Fees by term</h2>

        <FeesByTerm locale="en" initialData={pricingData} />

        <div className="mt-10 space-y-2 text-sm leading-relaxed text-muted-foreground">
          <LateFeeNote locale="en" />
          <p>
            Short-term enrolment cannot be combined with weeks and months. A maximum of two weeks of
            short-term enrolment can be booked per term, otherwise a month or a full term must be
            booked. A maximum of two months can be booked per term, otherwise a full term must be
            booked.
          </p>
          <p>
            If your child takes part in Malmö FF's football academy, the school helps with the entire application process and the terms that apply to participation. Malmö FF's football academy requires schooling via Sofia Distans and a school place at
            Swedish International School.
          </p>
        </div>

        <OnboardingNote className="mt-12 bg-card" locale="en" />

        <div className="mt-8 rounded-xl border border-border bg-sand p-6 sm:p-7">
          <h3 className="text-lg font-semibold">Unique benefits card</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Swedish International School works with Villa Padierna. When the partnership makes it
            possible, our families may receive a benefits card offering discounts and privileges in
            the area.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            This may include:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
            <li>Padel and tennis</li>
            <li>Golf</li>
            <li>Villa Padierna restaurants</li>
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Benefits are passed on within the scope of the partnership and may change over time. We
            cannot guarantee their availability or content in advance.
          </p>
        </div>
      </section>

      <Prose>
        <h2>What the fee covers</h2>
        <p>
          The termly fee covers teaching according to the Swedish curriculum with Swedish-speaking
          staff, premises and the school's regular activities. Lunch is served at school on regular school
          days. The registration
          fee is paid once per pupil on admission. Options such as after-school care, the football
          academy and the padel or tennis programme sit outside the termly fee and are listed above.
        </p>

        <h2>Payment and terms</h2>
        <p>
          The school fee must be paid before the student starts at the school. The fee for the spring term is due by 25 October and the fee for the autumn term by 1 May. If the student starts during a term, the full term fee is due within three working days of the invoice and before the first school day. Term dates are listed under{" "}
          <a className="text-primary link-quiet" href="/en/school-year-dates">school year dates</a>.
        </p>

        <h2>Family discount and short-term enrolment</h2>
        <p>
          Families with several children at the school receive a discount as listed above, calculated
          on the lowest fee. For families in Spain for only part of the year there is short-term
          enrolment, with a lower enrolment fee and a fee based on the length of stay. The{" "}
          <a className="text-primary link-quiet" href="/en/application">application</a> calculates
          the total as you fill in school form, period and options.
        </p>

        <h2>State funding</h2>
        <p>
          Families where a guardian is posted abroad for work can in some cases apply to the Swedish
          National Agency for Education for a grant towards distance study at lower and upper secondary
          level. The conditions are set by the agency — read more on the page about{" "}
          <a className="text-primary link-quiet" href="/en/swedish-national-agency-for-education">the Swedish National Agency for Education</a>.
        </p>
      </Prose>
      <FaqBlock items={faqFor("avgifter", "en")} locale="en" />


      <CtaBand
        title="Questions about fees?"
        text="Contact us and we will go through the fees for your family."
        locale="en"
      />
      <div className="h-20" />
    </>
  );
}
